import Axios, { HttpStatusCode, Method } from 'axios';
import { SearchQueryFilters } from 'src/models/CommonInterface';
import { IAsset } from 'src/models/CommonInterface';

// // Add these interfaces at the top of your file
// interface AssetResponse {
//   asset: {
//     id: number;
//     url: string;
//     assetId: string;
//   };
// }

// interface UploadResponse {
//   data: AssetResponse;
//   status: number;
// }

export class BaseAPI {
  static getExpenseByUserIdList(arg0: (result: any) => void) {
    throw new Error('Method not implemented.');
  }

  static getAPIBaseURI() {
    const BASE_API_URL = process.env.REACT_APP_API_URL;
    return BASE_API_URL;
  }

  static buildAPIURL(apiPath: string) {
    return `${BaseAPI.getAPIBaseURI()}${apiPath.startsWith('/') ? '' : '/'}${apiPath}`;
  }

  static getAuthHeaders(contentType: string) {
    const userDetailString = sessionStorage.getItem('UserDetail');
    if (!userDetailString) throw new Error('UserDetail not found in sessionStorage');

    const accessToken = JSON.parse(userDetailString)?.access_token;
    if (!accessToken) throw new Error('Access token not found in UserDetail');

    return {
      'Content-Type': contentType,
      Authorization: `Bearer ${accessToken}`
    };
  }

  /**
   * Decode JWT token and extract tenant_id from claims.
   * Returns tenant_id or null if not found/invalid.
   */
  static getTenantIdFromToken(): string | null {
    try {
      const userDetailString = sessionStorage.getItem('UserDetail');
      if (!userDetailString) return null;

      const accessToken = JSON.parse(userDetailString)?.access_token;
      if (!accessToken) return null;

      // JWT structure: header.payload.signature
      const parts = accessToken.split('.');
      if (parts.length !== 3) return null;

      // Decode base64 payload
      const payload = parts[1];
      const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const claims = JSON.parse(decodedPayload);

      // Extract tenant_id (could be tenantId or tenant_id in JWT)
      return claims.tenantId || claims.tenant_id || null;
    } catch (error) {
      console.error('Failed to decode JWT token:', error);
      return null;
    }
  }

  /**
   * Build a stable, publicly-accessible URL for a profile picture asset.
   * No auth required — secured by nanoID obscurity + tenant scoping.
   * Only assets uploaded with category='profile' are served by this endpoint.
   * Returns null when assetId is empty or tenant ID cannot be determined.
   */
  static buildProfilePictureURL(assetId: string | null | undefined): string | null {
    if (!assetId) return null;
    const tenantId = BaseAPI.getTenantIdFromToken();
    if (!tenantId) return null;
    return BaseAPI.buildAPIURL(`/public/t/${tenantId}/profile-picture/${assetId}`);
  }

  /**
   * Extract nanoID from a signed S3 URL and build the public streaming URL.
   * S3 path format: profile/<nanoID>-<filename> (nanoID is always NANO_ID_LENGTH chars).
   * NanoIDs in this project are 14 alphanumeric characters with embedded hyphens,
   * e.g. "AOW1bdW-ENtTDt". The S3 key filename starts with the nanoID followed by
   * a dash and the rest of the filename: "AOW1bdW-ENtTDt-timestamp.jpg".
   * Returns null if the URL can't be parsed, the extracted segment fails validation,
   * or the tenant ID is unavailable.
   */
  static buildProfilePictureURLFromSignedURL(signedUrl: string | null | undefined): string | null {
    /** Length of a nanoID as used in this project (e.g. "AOW1bdW-ENtTDt"). */
    const NANO_ID_LENGTH = 14;
    /** Valid nanoID characters: alphanumeric plus hyphens. */
    const NANO_ID_PATTERN = /^[A-Za-z0-9-]{14}$/;

    if (!signedUrl) return null;
    try {
      // Extract the path segment after the bucket host, e.g. /profile/AOW1bdW-ENtTDt-timestamp.jpg
      const url = new URL(signedUrl);
      // pathname = /profile/AOW1bdW-ENtTDt-timestamp.jpg (possibly percent-encoded)
      const filename = url.pathname.split('/').pop();
      if (!filename) return null;
      const decoded = decodeURIComponent(filename); // "AOW1bdW-ENtTDt-timestamp.jpg"
      // The nanoID occupies exactly the first NANO_ID_LENGTH characters of the filename.
      const nanoId = decoded.substring(0, NANO_ID_LENGTH);
      // Validate: must be exactly NANO_ID_LENGTH chars and match allowed character set.
      if (!NANO_ID_PATTERN.test(nanoId)) return null;
      return BaseAPI.buildProfilePictureURL(nanoId);
    } catch {
      return null;
    }
  }

  /**
   * Convenience: accepts either a nanoID or a signed S3 URL.
   * Prefers the public streaming URL either way.
   */
  static resolveProfilePictureURL(
    assetId: string | null | undefined,
    signedUrl: string | null | undefined
  ): string | null {
    return (
      BaseAPI.buildProfilePictureURL(assetId) ||
      BaseAPI.buildProfilePictureURLFromSignedURL(signedUrl) ||
      null
    );
  }

  static async request(
    method: Method,
    apiPath: string,
    data: any = null,
    callback?: (response: any) => void,
    contentType: string = 'application/json',
    params?: any,
    useAuth: boolean = true
  ) {
    try {
      let headers: any;

      if (data instanceof FormData) {
        headers = useAuth ? BaseAPI.getAuthHeaders('multipart/form-data') : {};
      } else {
        headers = useAuth ? BaseAPI.getAuthHeaders(contentType) : { 'Content-Type': contentType };
      }

      if (method === 'DELETE') {
        delete headers['Content-Type'];
      }

      const axiosConfig: any = {
        method,
        url: BaseAPI.buildAPIURL(apiPath),
        headers,
        params
      };

      if (method !== 'DELETE') {
        axiosConfig.data = data;
      }

      const response = await Axios(axiosConfig);

      if (callback) {
        callback(response);
      }
      return response;
    } catch (error: any) {
      if (error.response) {
        const errorResponse: any = {
          status: error.response.status,
          message: error.response.data?.message || error.response.data?.error || error.response.data?.reason || 'General server error'
        };
        if (callback) {
          callback(errorResponse);
        }
        return errorResponse;
      } else {
        return undefined;
      }
    }
  }

  static async login(formdata: any, callback: any) {
    return this.request('POST', 'auth/login', formdata, callback);
  }

  static async logout(callback: any) {
    return this.request('POST', 'auth/logout', callback);
  }

  static async validateToken() {
    return this.request('GET', 'auth/validate-token');
  }

  static async userDetails(formdata: any, callback: any) {
    return this.request('POST', 'auth/register', formdata, callback);
  }

  static async workLogDetails(formdata: any, callback: any) {
    return this.request('POST', '/worklog', formdata, callback);
  }

  static async tickettoolDetails(formdata: any, callback: any) {
    return this.request('POST', '/tickettool', formdata, callback);
  }

  static async tickettoolBulkUpdate(formdata: any[], callback: any) {
    return this.request('POST', '/tickettool/bulkupdate', formdata, callback);
  }

  static async tickettoolList(filter: any, callback?: (response: any, filter?: any) => void) {
    return this.request('GET', '/tickettool', null, (response) => callback?.(response, filter), 'application/json', filter);
  }

  static async tickettoolStatusCount(filter: any, callback: any) {
    return this.request('GET', '/tickettool/statuscount', null, callback, null, filter);
  }

  static async tickettoolStatusGroupingCount(filter: any, callback: any) {
    return this.request('GET', '/tickettool/statuscount/groupby', null, callback, null, filter);
  }

  static async tickettoolListAll(filter: any, callback?: any, p0?: number[]) {
    return this.request('POST', '/tickettool/search', filter, callback);
  }

  static async tickettoolHistoryList(filter: any, callback?: any) {
    return this.request('POST', '/tickettool/history', filter, callback);
  }

  static async getWorklogDetails(callback: any) {
    return this.request('GET', '/worklog', null, callback);
  }

  static async getallbiometricData(callback: any) {
    return this.request('GET', '/bio-metric-connection/getAllAttendanceDetails', null, callback);
  }

  static async getResignationsForSupervisor(supervisorPartyId: string) {
    return this.request('GET', `/resignation/supervisor/${supervisorPartyId}`);
  }

  static async getResignations(): Promise<any> {
    const response = await this.request('GET', '/resignation');
    return response ? response.data : response;
  }

  static async submitResignation(data: any, callback: any) {
    return this.request('POST', '/resignation', data, callback);
  }

  static async getExitInterview(resignationId: string, callback: any) {
    return this.request('GET', `/exit-interviews/resignation/${resignationId}`, null, callback);
  }

  static async getExitInterviewByResignationId(resignationId: string, callback: any) {
    return this.request('GET', `/exit-interviews/resignation/${resignationId}`, null, callback);
  }

  static async submitExitInterview(exitInterviewData: any, callback: any) {
    return this.request('POST', '/exit-interviews', exitInterviewData, callback);
  }

  static async updateExitInterview(id: string, exitInterviewData: any, callback: any) {
    return this.request('PATCH', `/exit-interviews/${id}`, exitInterviewData, callback);
  }

  static async getResignationList(data: any, callback: any) {
    return this.request('GET', '/resignation', null, callback);
  }

  static async getResignationByEmployeeID(empId: any, callback: any) {
    return this.request('GET', `/resignation/employee/${empId}`, null, callback);
  }

  static async getResignationHistory(id: string, callback: any) {
    return this.request('GET', `/resignation/history/${id}`, {}, callback);
  }
  static async updateResignation(id: string, updateData: any, callback: any) {
    return this.request('PUT', `/resignation/${id}`, updateData, callback);
  }
  // static async updateResignation(id, updateData, callback) {
  //   await Axios.put(BaseAPI.buildAPIURL(`/resignation/${id}`), updateData, {
  //     headers: {
  //       'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
  //       'Content-Type': 'application/json'
  //     }
  //   })
  //   .then((response) => {
  //     callback(response);
  //   })
  //   .catch((error) => {
  //     if (error.response) {
  //       callback({
  //         status: error.response.status,
  //         message: error.response.data?.error
  //           ? error.response.data?.error
  //           : "Failed to update resignation",
  //         response: error.response,
  //       });
  //     } else {
  //       callback({
  //         status: 500,
  //         message: "An error occurred while updating resignation",
  //       });
  //     }
  //   });
  // }

  static async getResignationById(id: string, callback: any) {
    return this.request('GET', `/resignation/${id}`, null, callback);
  }

  static async getAllProjectsWithoutPagination(callback: any) {
    return this.request('GET', `/project/all`, null, callback);
  }
  static async getAllProjectsList(callback: any) {
    return this.request('GET', '/project-details/projects/list', null, callback);
  }

  static async newRoles(formdata: any, callback: any) {
    return this.request('POST', '/access-operation', formdata, callback);
  }

  static async getUserRoleDatabyRoleID(id: number, callback: any) {
    return this.request('GET', `sub-category/${id}`, null, callback);
  }

  static async getRolesByID(id: string, callback: any) {
    return this.request('GET', `/roles/role/${id}`, null, callback);
  }

  static async getRolesIdOperation(id: string, callback: any) {
    return this.request('GET', `/access-operation/${id}/role/operation`, null, callback);
  }

  // ===== V1 PERMISSIONS API (NEW RBAC SYSTEM) =====
  static async getMyPermissionsV2(callback?: any) {
    return this.request('GET', 'v1/permissions/me', null, callback);
  }

  static async getPermissionCatalogV2(callback?: any) {
    return this.request('GET', 'v1/permission-catalog', null, callback);
  }

  static async getRolePermissionsV2(roleId: number, callback?: any) {
    return this.request('GET', `v1/roles/${roleId}/permissions`, null, callback);
  }

  static async updateRolePermissionsV2(roleId: number, entityOperationIds: number[], callback?: any) {
    return this.request('PUT', `v1/roles/${roleId}/permissions`, { entityOperationIds }, callback);
  }
  // ===== END V1 PERMISSIONS API =====


  static async getWorklogDetailsdata(query: any, callback: any) {
    if (!Object.keys(query).length) return;

    const queryParams = new URLSearchParams(query).toString();
    return this.request('GET', `/worklog/list?${queryParams}`, null, callback);
  }

  static async getWorklogDetailId(id: string) {
    return this.request('GET', `/worklog/${id}`);
  }

  static async getTicketToolId(id: string) {
    const response = await this.request('GET', `/tickettool/${id}`);
    if (response?.status === HttpStatusCode.Ok) {
      return response?.data;
    }
    return false;
  }

  static async editTicketTool(id: string) {
    return this.request('PUT', `/tickettool/${id}`);
  }

  static async getWorklogDetailID(id: string, callback: any) {
    return this.request('GET', `/worklog/${id}`, null, callback);
  }

  static async listCommentData(callback: any) {
    return this.request('GET', '/ticketcommentstatus', null, callback);
  }

  static async getCommentDataByTicketId(id: any, callback: any) {
    return this.request('GET', `/ticketcommentstatus/ticket/${id}`, null, callback);
  }

  static async deactivateTicketComment(id: string, callback: any) {
    return this.request('DELETE', `/ticketcommentstatus/${id}`, null, callback);
  }

  static async getUserDetails(callback: any) {
    return this.request('GET', `/users`, null, callback);
  }

  static async getUserDetailsByEmail(email, callback: any) {
    const params = { email: email };
    return this.request('GET', `/users`, null, callback, null, params);
  }

  static async getUserDetailsById(id, callback: any) {
    const params = { id: id };
    return this.request('GET', `/users`, null, callback, null, params);
  }

  static async updateUserDetails(id: number, formData: any, callback: any) {
    return this.request('PUT', `/users/${id}/update`, formData, callback);
  }

  static async updateUserRoles(id: number, formData: any, callback: any) {
    return this.request('PUT', `/user-roles/${id}`, formData, callback);
  }

  static async updateWorklogDetails(id: number, formData: any, callback: any) {
    return this.request('PUT', `/worklog/${id}`, formData, callback);
  }

  static async updateToolkitDetails(id: number, formData: any, callback: any) {
    return this.request('PUT', `/tickettool/${id}`, formData, callback);
  }

  static async createTicketToolHistory(id: number, callback: any) {
    return this.request('PUT', `/tickettool/${id}/history`, callback);
  }

  static async getTicketToolHistoryById(id: number, callback: any) {
    return this.request('GET', '/tickettool/history/' + id, null, callback);
  }

  static async getTicketToolHistory(payload: any, callback: any) {
    const queryParams = new URLSearchParams(payload).toString();
    return this.request('GET', `/tickettool/history/all?${queryParams}`, null, callback);
  }

  static async addCommand(formData: any, callback: any) {
    return this.request('POST', '/ticketcommentstatus', formData, callback);
  }

  static async addCommandImg(formData: any, callback: any) {
    return this.request('POST', '/ticketcommentstatus/img', formData, callback);
  }

  static async ticketCommandMail(formData: any, callback: any) {
    return this.request('POST', '/ticketcommentstatus/sendmail', formData, callback);
  }

  static async deleteUser(id: number, callback: any) {
    return this.request('DELETE', `/delete/${id}`, null, callback);
  }

  static async deleteEmployee(ids: number[], callback: any) {
    try {
      // Use the first ID in the path for routing
      const id = ids[0];

      // Make sure we're sending the proper payload
      const response = await this.request('DELETE', `/employee/${id}`, { ids });

      callback?.({ status: 200, message: 'Employees deleted successfully' });
    } catch (error) {
      console.error('Error in deleteEmployee:', error);
      callback?.({
        status: error.status || 500,
        message: error.message || 'General server error'
      });
    }
  }

  // Add these methods to your existing BaseAPI class

  // Create Template
  static async createTemplate(templateData: any, callback: any) {
    return this.request('POST', '/offer-letters/templates', templateData, callback);
  }

  // Update Template
  static async updateTemplate(id: number, templateData: any, callback: any) {
    return this.request('PUT', `/offer-letters/templates/${id}`, templateData, callback);
  }

  // Delete Template
  static async deleteTemplate(id: number, callback: any) {
    return this.request('DELETE', `/offer-letters/templates/${id}`, {}, callback);
  }

  // Get Single Template
  static async getTemplateById(id: number, callback: any) {
    return this.request('GET', `/offer-letters/templates/${id}`, {}, callback);
  }

  // Get Templates by Type (content or salary)
  static async getTemplatesByType(type: 'content' | 'salary', callback: any) {
    return this.request('GET', `/offer-letters/templates/type/${type}`, {}, callback);
  }

  // static async deleteEmployee(ids: number[], callback: any) {
  //   try {
  //     // For consistency, use the first ID in the path but send all IDs in the body
  //     const firstId = ids[0];
  //     const response = await this.request("DELETE", `/employee/${firstId}`, { ids });
  //     callback?.({ status: 200, message: 'Employees deleted successfully' });
  //   } catch (error) {
  //     callback?.({
  //       status: error.status || 500,
  //       message: error.message || "General server error",
  //     });
  //   }
  // }

  // static async deleteEmployee(ids: number[], callback: any) {
  //   try {
  //     const promises = ids.map(id => this.request("DELETE", `/employee/${id}`));
  //     await Promise.all(promises);
  //     callback?.({ status: 200, message: 'Employees deleted successfully' });
  //   } catch (error) {
  //     callback?.({
  //       status: error.status || 500,
  //       message: error.message || "General server error",
  //     });
  //   }
  // }

  static async deleteWorklog(id: number, callback: any) {
    return this.request('DELETE', `/worklog/${id}`, null, callback);
  }

  static async deleteTicketTool(id: number, callback: any) {
    return this.request('DELETE', `/tickettool/delete/${id}`, null, callback);
  }

  static async activateTicketTool(id: number, callback: any) {
    return this.request('DELETE', `/tickettool/restore/${id}`, null, callback);
  }

  static async getUserEditDetails(callback: any) {
    return this.request('GET', '/user_details', null, callback);
  }

  static async getEmployeeNameDetails(callback?: (response: any, filter?: any) => void, filter?: any) {
    return this.request('GET', '/employee-details', null, callback, filter);
  }

  static async getEmployeeData(callback: any) {
    return this.request('GET', '/employee-details', null, callback);
  }

  static async addRoles(formData: any, callback: any) {
    return this.request('POST', '/user-roles/addRoles', formData, callback);
  }

  static async getSubCategory(callback: any) {
    return this.request('GET', '/sub-category', null, callback);
  }

  static async getCategory(callback: any) {
    return this.request('GET', '/category', null, callback);
  }

  static async updateSubCategoryStatus(payload: any, id: number, callback: any) {
    return this.request('PUT', `/sub-category/status/${id}`, payload, callback);
  }

  static async updateCategory(payload: any, id: number, callback: any) {
    return this.request('PATCH', `/category/${id}`, payload, callback);
  }

  static async getRoles(callback: any) {
    return this.request('GET', 'roles/role-details', null, callback);
  }

  static async getRolesById(id, callback: any) {
    return this.request('GET', `roles/${id}`, null, callback);
  }

  static async getRolesData(callback: any) {
    return this.request('GET', '/user-roles', null, callback);
  }

  static async getPersonalInformationID(id: any, callback: any) {
    return this.request('GET', `/employee/${id}`, null, callback);
  }

  static async getEmployeeProfile(id: string, callback: any) {
    return this.request('GET', `/employee/profile/${id}`, null, callback);
  }

  static async getEmployeeByPartyID(id: number, callback: any) {
    return this.request('GET', `/employee/party/${id}`, null, callback);
  }

  static async getAllPersonalInformationID(id: any, callback: any) {
    return this.request('GET', `/employee/all/${id}`, null, callback);
  }

  static async getAllEmployeeByPartyID(id: number, callback: any) {
    return this.request('GET', `/employee/all/party/${id}`, null, callback);
  }

  static async getPersonalInformation(callback: any) {
    return this.request('GET', '/employee', null, callback);
  }

  static async getAllPersonalInformation(callback: any, params?: any) {
    return this.request('GET', '/employee/all', null, callback, 'application/json', params);
  }

  static async getActiveEmployee(callback: any) {
    return this.request('GET', '/employee/active-employee', null, callback);
  }

  /**
   * Lightweight employee lookup for autocomplete/dropdowns
   * Uses Go backend if USE_GO_EMPLOYEE_LOOKUP is enabled, otherwise falls back to NestJS
   * @param params - { active?: boolean, department?: string, limit?: number, fieldsets?: string }
   */
  static async getEmployeeLookup(params?: any, callback?: any) {
    // Both Go and NestJS serve /employee/lookup with identical response shape
    // (flat object with top-level partyId). The flag only controls routing preference.
    return this.request('GET', '/employee/lookup', null, callback, 'application/json', params);
  }

  /**
   * Search employees for autocomplete (lightweight)
   * Uses Go backend if USE_GO_EMPLOYEE_LOOKUP is enabled, otherwise falls back to NestJS
   * @param query - Search query string
   * @param params - { active?: boolean, limit?: number, fieldsets?: string }
   */
  static async searchEmployeeLookup(query: string, params?: any, callback?: any) {
    // Dynamic import to avoid circular dependencies
    const { USE_GO_EMPLOYEE_LOOKUP } = await import('../utils/envConfig');

    if (USE_GO_EMPLOYEE_LOOKUP) {
      // Use Go backend lightweight search endpoint
      const searchParams = { q: query, ...params };
      return this.request('GET', '/employee/lookup/search', null, callback, 'application/json', searchParams);
    } else {
      // Fallback to NestJS full employee list (client-side filtering needed)
      return this.request('GET', '/employee/all', null, callback);
    }
  }

  static async addAttendenceUpload(formData: any, callback: any) {
    return this.request('POST', '/user-data', formData, callback, 'multipart/form-data');
  }
  static async assetBulkUpload(URL: any, formdata: any, callback: any) {
    return this.request('POST', `/${URL}/upload`, formdata, callback, 'multipart/form-data');
  }

  static async getAssetsByIds(assetIds: string[]) {
    const params = new URLSearchParams();
    assetIds.forEach((id) => params.append('ids[]', id));
    return this.request('GET', `/asset/bulkGet?${params.toString()}`, null, null);
  }

  static async getExpenseDetailByUserId(userId: any, expenseId: any, callback: any) {
    return this.request('GET', `/expenses/${userId}/${expenseId}`, null, callback);
  }

  static async addPersonalInformation(formData: any, callback: any) {
    return this.request('POST', '/employee', formData, callback);
  }

  static async deleteDocument(id: number, callback: any) {
    return this.request('DELETE', `/document/${id}`, null, callback);
  }

  static async updateDocument(id: number, formData: any, callback: any) {
    return this.request('PUT', `/document/${id}`, formData, callback);
  }

  static async addDocument(id: number, formData: any, callback: any) {
    return this.request('POST', '/document', formData, callback);
  }

  static async updatePersonalInformation(id: number, formData: any, callback: any) {
    return this.request('PUT', `/employee/${id}/update`, formData, callback);
  }

  static async addEmployeeUpload(formData: any, callback: any) {
    return this.request('POST', '/user-data', formData, callback, 'multipart/form-data');
  }

  static async getEmployeeDetails(callback?: (response: any, filter?: any) => void, filter?: any) {
    return this.request('GET', '/user-data', null, callback, null, filter);
  }

  static async getSettingsDetails(callback: any) {
    return this.request('GET', '/settings', null, callback);
  }

  static async getLogoAssetId(callback: any) {
    return this.request('GET', '/settings/asset-id', null, callback, 'application/json', null, false);
  }

  static async getDepartmentList(callback: any) {
    return this.request('GET', '/department', null, callback);
  }

  static async deleteDepartment(id: number, callback: any) {
    return this.request('DELETE', `/department/${id}`, null, callback);
  }

  static async deleteDesignation(id: number, callback: any) {
    return this.request('DELETE', `/designation/${id}`, null, callback);
  }

  static async deleteJobDetailsHistory(id: number, callback: any) {
    return this.request('DELETE', `/job-details-history/${id}`, null, callback);
  }

  static async getDesignationList(callback: any) {
    return this.request('GET', '/designation', null, callback);
  }

  static async getDesignationByID(id: number, callback: any) {
    return this.request('GET', `/designation/get/${id}`, null, callback);
  }

  static async getDepartmentByID(id: number, callback: any) {
    return this.request('GET', `/department/get/${id}`, null, callback);
  }

  static async deleteWorkLocation(id: number, callback: any) {
    return this.request('DELETE', `/work-locations/${id}`, null, callback);
  }

  static async getWorkLocationList(callback: any) {
    return this.request('GET', '/work-locations', null, callback);
  }

  static async getWorkLocationByID(id: number, callback: any) {
    return this.request('GET', `/work-locations/get/${id}`, null, callback);
  }

  static async settingDetails(formdata: any, callback: any) {
    return this.request('POST', '/settings', formdata, callback);
  }

  static async settingWorkLocationDetails(formdata: any, callback: any) {
    return this.request('POST', '/work-locations', formdata, callback);
  }

  static async updateWorkLocation(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/work-locations/${id}`, formdata, callback);
  }

  static async settingDepartmentsDetails(formdata: any, callback: any) {
    return this.request('POST', '/department', formdata, callback);
  }

  static async settingDesignationDetails(formdata: any, callback: any) {
    return this.request('POST', '/designation', formdata, callback);
  }

  static async updateDesignationDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/designation/${id}`, formdata, callback);
  }

  static async updateDepartmentsDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/department/${id}`, formdata, callback);
  }
  static async updateAttendanceStatus(payload: any, callback: any) {
    return this.request('PUT', '/shift-coverage/attendance/update-status', payload, callback);
  }

  static async updateSettingsDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/settings/${id}`, formdata, callback);
  }

  static async createLeave(formdata: any, callback: any) {
    return this.request('POST', '/leaveRequest', formdata, callback);
  }

  static async leaveCreate(formdata: any, callback: any) {
    return this.request('POST', '/leave_transaction', formdata, (response) => {
      callback(response);
    });
  }

  static async getMailOptions(formdata: any, callback: any) {
    const queryParams = new URLSearchParams(formdata).toString();
    return this.request('GET', `/mailoptions?${queryParams}`, null, callback);
  }

  static async getMailOptionsById(id: any, callback: any) {
    return this.request('GET', `/mailoptions/${id}`, null, callback);
  }

  static async saveMailOption(formdata: any, callback: any) {
    return this.request('POST', `/mailoptions/`, formdata, callback);
  }

  static async updateMailOption(id: any, formdata: any, callback: any) {
    return this.request('PATCH', `/mailoptions/${id}`, formdata, callback);
  }

  static async getAllLeaveType(callback: any) {
    return this.request('GET', '/leave_type/leave', null, callback);
  }
  static async getLeaveData(callback: any) {
    return this.request('GET', '/leave_transaction', null, callback);
  }

  static async getLeaveDataByUser(userId: number, callback: any) {
    return this.request('GET', `/leave_transaction/user/${userId}`, null, callback);
  }

  static async searchMyLeaves(filters: any, callback: any) {
    return this.request('POST', '/leave_transaction/search-my-leaves', filters, callback);
  }

  static async searchTeamLeaves(filters: any, callback: any) {
    return this.request('POST', '/leave_transaction/search-team-leaves', filters, callback);
  }

  // static async getRequestedLeaveBalance(userId: number, leaveType: string, callback: any) {
  //   return this.request("GET", `/leave_transaction/requested-balance/user/${userId}/leaveType/${leaveType}`, null, callback);
  // }

  static async getLeaveBalances(callback: any) {
    return this.request('GET', '/leave-balance', null, callback);
  }

  static async searchMyLeaveBalance(filters: any, callback: any) {
    return this.request('POST', '/leave-balance/search-my-leave-balance', filters, callback);
  }

  static async searchTeamLeaveBalance(filters: any, callback: any) {
    return this.request('POST', '/leave-balance/search-team-leave-balance', filters, callback);
  }

  static async updateLeaveRequest(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/leave_transaction/${id}`, formdata, callback);
  }

  static async cancelIndividualDay(formdata: any, callback: any) {
    return this.request('POST', '/leave_transaction/cancel-individual-day', formdata, (response) => {
      callback(response);
    });
  }

  static async cancelIndividualDate(id: number, cancelData: any, callback: any) {
    return this.request('PATCH', `/leave_transaction/${id}/cancel-individual-date`, cancelData, callback);
  }

  static async approveIndividualCancellation(id: number, action: 'approve' | 'decline', data: any, callback: any) {
    return this.request('PATCH', `/leave_transaction/${id}/individual-cancellation/${action}`, data, callback);
  }

  static async getByIdLeaveData(id: string) {
    try {
      const response = await this.request('GET', `/leave_transaction/${id}`);
      if (response?.status === HttpStatusCode.Ok) {
        return response?.data;
      }
      return false;
    } catch (error) {
      return {
        status: error.response?.status || 500,
        message: error.response?.data?.error || 'General server error'
      };
    }
  }

  static async getByIdLeaveList(id: number, callback: any) {
    return this.request('GET', `/leave_transaction/${id}`, null, callback);
  }

  static async getByIdLeaveRequest(query: any, callback: any) {
    if (!Object.keys(query).length) return;

    const queryParams = new URLSearchParams(query).toString();
    return this.request('GET', `/leave_transaction/?${queryParams}`, null, callback);
  }

  static async createLeavetype(formdata: any, callback: any) {
    return this.request('POST', '/leave_type', formdata, callback);
  }

  static async getLeaveTypeListData(callback: any) {
    return this.request('GET', '/leave_type/all', null, callback);
  }

  static async deleteLeaveType(id: number, callback: any) {
    return this.request('DELETE', `/leave_type/delete/${id}`, null, callback);
  }

  static async updateLeaveTypeDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/leave_type/${id}`, formdata, callback);
  }

  static async createCompOff(formdata: any, callback: any) {
    return this.request('POST', '/comp_off', formdata, callback);
  }
  // Get all comp off records
  static async getAllCompOffs(callback: any) {
    return this.request('GET', '/comp_off', null, callback);
  }

  // Get comp off by ID
  static async getCompOffById(id: number, callback: any) {
    return this.request('GET', `/comp_off/${id}`, null, callback);
  }

  // Get comp offs for a specific user
  static async getCompOffsByUser(partyId: number, callback: any) {
    return this.request('GET', `/comp_off/user/${partyId}`, null, callback);
  }

  // Update comp off
  static async updateCompOff(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/comp_off/${id}`, formdata, callback);
  }

  // Update request status
  static async updateCompOffStatus(id: number, status: number, callback: any) {
    return this.request('PATCH', `/comp_off/${id}/request-status`, { status }, callback);
  }

  // Delete comp off
  static async deleteCompOff(id: number, callback: any) {
    return this.request('DELETE', `/comp_off/${id}`, null, callback);
  }

  static async getAllOfferLetterTemplates(callback: any) {
    return this.request('GET', `/offer-letters/templates`, null, callback);
  }

  // Get content templates
  static async getContentTemplates(callback: any) {
    return this.request('GET', `/offer-letters/templates/type/content`, null, callback);
  }

  // Get salary templates
  static async getSalaryTemplates(callback: any) {
    return this.request('GET', `/offer-letters/templates/type/salary`, null, callback);
  }

  // Create offer letter
  static async createOfferLetter(formdata: any, callback: any) {
    return this.request('POST', `/offer-letters`, formdata, callback);
  }

  // Generate preview (without saving)
  static async generateOfferLetterPreview(formdata: any, callback: any) {
    return this.request('POST', `/offer-letters/preview`, formdata, callback);
  }

  // Get all offer letters
  static async getAllOfferLetters(callback: any) {
    return this.request('GET', `/offer-letters`, null, callback);
  }

  // Get offer letter by ID
  static async getOfferLetterById(id: number, callback: any) {
    return this.request('GET', `/offer-letters/${id}`, null, callback);
  }

  // Get ACTIVE content templates only (for Offer Letter Generator)
  static async getActiveContentTemplates(callback: any) {
    return this.request('GET', `/offer-letters/templates/type/content/active`, null, callback);
  }

  // Get ACTIVE salary templates only (for Offer Letter Generator)
  static async getActiveSalaryTemplates(callback: any) {
    return this.request('GET', `/offer-letters/templates/type/salary/active`, null, callback);
  }
  // Update offer letter by ID
  static async updateOfferLetter(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/offer-letters/${id}`, formdata, callback);
  }

  // Delete offer letter by ID
  static async deleteOfferLetter(id: number, callback: any) {
    return this.request('DELETE', `/offer-letters/${id}`, null, callback);
  }
  static async getPartyDetails(callback: any) {
    return this.request('GET', '/users', null, callback);
  }
  static async getPartyDetailsByEmail(email, callback: any) {
    const param = { email: email };
    return this.request('GET', '/users', null, callback, null, param);
  }
  static async getPartyDetailsById(id, callback: any) {
    const param = { id: id };
    return this.request('GET', '/users', null, callback, null, param);
  }
  static async getAllStatusUpdate(callback?: any) {
    return this.request('GET', '/status-updates', null, callback);
  }
  static async getAllStatusUpdateNew(formdata, callback?: any) {
    return this.request('POST', '/status-updates/search', formdata, callback);
  }
  static async getStatusUpdateById(id: string, callback?: any) {
    return this.request('GET', `/status-updates/${id}`, null, callback);
  }

  static async getStatusUpdateByTicketId(id: string, callback?: any) {
    return this.request('GET', `/status-updates/ticketid/${id}`, null, callback);
  }

  static async submitContactForm(formdata: any, callback: any) {
    return this.request('POST', '/contact-us', formdata, callback);
  }

  static async EmployeeNameData(query: any, callback: any) {
    if (!Object.keys(query).length) return;

    const queryParams = new URLSearchParams(query).toString();
    return this.request('GET', `/employee-details?${queryParams}`, null, callback);
  }

  static async UserData(query: any, callback: any) {
    if (!Object.keys(query).length) return;

    const queryParams = new URLSearchParams(query).toString();
    return this.request('GET', `/user-data?${queryParams}`, null, callback);
  }

  static async getUserList(callback?: any) {
    return this.request('GET', '/users', null, callback);
  }

  static async getByIdUserList(id: number, callback: any) {
    return this.request('GET', `/users/${id}`, null, callback);
  }

  static async getProfileImage(id: number, callback: any) {
    return this.request('GET', `/asset/${id}`, null, callback);
  }

  static async fetchUserProfileImage(userId: number) {
    try {
      const response = await this.request('GET', `/api/assets/user/${userId}/profile-image`);
      return response.data.data; // Contains { id, url, title, contentType, uploadedStatus }
    } catch (error) {
      console.error('Error fetching profile image:', error);
      return null;
    }
  }

  static async getAllDocuments(callback: any) {
    return this.request('GET', `/document`, null, callback);
  }

  static async getDocumentById(id: number, callback: any) {
    return this.request('GET', `/document/${id}`, null, callback);
  }
  static async getAssetsWithPagination(page: number, limit: number, filters: any, callback: any) {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...filters
    }).toString();

    return this.request('GET', `/asset/paginated?${queryParams}`, null, callback);
  }

  static async updateUserInformation(id: number, formdata: any, callback: any) {
    return this.request('PUT', `users/${id}/update`, formdata, callback);
  }

  static async getEmployeeListFilter(callback: any, filters?: any) {
    const queryParams = filters && Object.keys(filters).length ? new URLSearchParams(filters).toString() : '';
    return this.request('GET', `/users${queryParams ? `?${queryParams}` : ''}`, null, callback);
  }

  static async getEmployeeFilter(callback: any, filter?: any) {
    const queryParams = filter && Object.keys(filter).length ? new URLSearchParams(filter).toString() : '';
    return this.request('GET', `/users${queryParams ? `?${queryParams}` : ''}`, null, callback);
  }

  static async getEmployeeFilterr(callback: any) {
    return this.request('GET', '/users', null, callback);
  }

  static async getCodeReference(callback?: (response: any) => void) {
    return this.request('GET', '/code-table', null, callback);
  }

  static async getEmployeeRole(callback: any) {
    return this.request('GET', '/roles/role-details', null, callback);
  }

  static async getRequestLeaveBalances(userId: number, leaveType: string, callback: any) {
    return this.request('GET', `/leave_transaction/user/${userId}/leaveType/${leaveType}`, null, callback);
  }

  static async getUserLeaveBalancesByLeaveType(userId: number, leaveType: string, callback: any) {
    return this.request('GET', `/leave-balance/user/${userId}/leaveType/${leaveType}`, null, callback);
  }
  static async updateRequestedLeave(employeeId: number, leaveTypeId: number, days: number, isAdd: boolean, callback: any) {
    return this.request('POST', `/leave-balance/update-requested-leave/${employeeId}/leaveType/${leaveTypeId}`, { days, isAdd }, callback);
  }

  // Inside your BaseAPI class

  static async createLeaveAdjustment(payload: any, callback: any) {
    return this.request('POST', `/leave-adjustments`, payload, callback);
  }

  static async getAllLeaveAdjustments(callback: any) {
    return this.request('GET', '/leave-adjustments', null, callback);
  }
  static async getRequestedLeaveBalance(userId: number, leaveTypeId: number, callback: any) {
    return this.request('GET', `/leave-balance/requested-leave/${userId}/leaveType/${leaveTypeId}`, null, callback);
  }

  static async updateRequestedLeaveBalance(employeeId: number, leaveTypeId: number, days: number, isAdd: boolean, callback: any) {
    const data = { days, isAdd };
    return this.request('PATCH', `/leave-balance/update-requested-leave/${employeeId}/leaveType/${leaveTypeId}`, data, callback);
  }

  static async createIndividualCancelRequest(formdata: any, callback: any) {
    return this.request('POST', '/leave_individual_cancel_request', formdata, callback);
  }
  static async updateIndividualLeaveDate(leaveId: number, formdata: any, callback: any) {
    return this.request('PUT', `/leave_transaction/${leaveId}/individual_date`, formdata, callback);
  }

  static async updateLeaveBalanceOnStatusChange(employeeId: number, leaveTypeId: number, updateData, callback: any) {
    return this.request('PUT', `/leave-balance/update-on-status-change/${employeeId}/leaveType/${leaveTypeId}`, updateData, callback);
  }
  // static async updateLeaveBalanceOnStatusChange(employeeId: number, leaveTypeId: number, days: number, status: string, callback: any) {
  //   const data = { days, status };
  //   return this.request("PATCH", `/leave-balance/update-on-status-change/${employeeId}/leaveType/${leaveTypeId}`, data, callback);
  // }

  static async updateLeaveBalanceById(
    id: number,
    updateData: { leaveTypeId: number; leaveDeducted: number },
    callback: (result: any) => void
  ) {
    try {
      const result = await this.request('PATCH', `/leave-balance/${id}`, updateData); // assuming this.request returns a Promise
      callback(result); // Call the callback with the result
    } catch (error) {
      callback({ status: 500, message: 'An error occurred' });
    }
  }

  static async getUserLeaveBalances(userId: any, callback: any) {
    return this.request('GET', `/leave-balance/user/${userId}`, null, callback);
  }

  static async getCategoryFind(currentPath: string, callback: any) {
    const formattedPath = currentPath.charAt(0).toUpperCase() + currentPath.slice(1);
    return this.request('GET', `/sub-category/role/operation?category=${formattedPath}`, null, callback);
  }

  static async addCodeReference(formData: any, callback: any) {
    return this.request('POST', '/code-table', formData, callback);
  }

  static async updateCodeReference(formdata: any, callback: any) {
    return this.request('PUT', `/code-table`, formdata, callback);
  }

  static async forgotPassword(formdata: any, callback: any) {
    return this.request('POST', '/auth/forget-password', formdata, callback, 'application/json', null, false);
  }
  static async resetPassword(formdata: any, callback: any) {
    return this.request('PUT', '/auth/reset-password', formdata, callback, 'application/json', null, false);
  }
  // Client related API Calls

  static async getClientData(callback: any, filter?: any) {
    const queryParams = filter ? new URLSearchParams(filter).toString() : '';
    return this.request('GET', `/clients${queryParams ? `?${queryParams}` : ''}`, null, callback);
  }

  static async getClientsLookup(callback: any) {
    return this.request('GET', `/v1/clients/lookup`, null, callback);
  }

  static async getClientDataById(id, callback: any) {
    return this.request('GET', `/clients/${id}`, null, callback);
  }

  static async createClientData(formdata: any, callback: any) {
    return this.request('POST', `/clients`, formdata, callback);
  }

  static async updateClientDataById(formdata: any, id, callback: any) {
    return this.request('PUT', `/clients/${id}`, formdata, callback);
  }

  static async updateContactPersonById(formdata: any, id, callback: any) {
    return this.request('PUT', `/clients/${id}/contactperson`, formdata, callback);
  }

  static async allProject(callback: any) {
    return this.request('GET', `/project`, null, callback);
  }

  static async deleteProjects(id: number, callback: any) {
    return this.request('DELETE', `/project/${id}`, undefined, callback);
  }

  static async deleteTickets(id: string, callback: any) {
    return this.request('DELETE', `/tickets/${id}`, null, callback);
  }

  static async ticketList(filter: any, callback: any) {
    const queryParams = new URLSearchParams(filter).toString();
    return this.request('GET', `/tickets?${queryParams}`, null, callback);
  }

  static async addComment(formdata: any, callback: any) {
    return this.request('POST', '/status', formdata, callback, 'multipart/form-data');
  }

  static async getCommentList(callback: any) {
    return this.request('GET', '/status', null, callback);
  }

  static async getTicketByID(id: string, callback: any) {
    return this.request('GET', `/tickets/${id}`, null, callback);
  }

  static async userList(filter: any, callback: any) {
    const queryParams = new URLSearchParams(filter).toString();
    return this.request('GET', `/users${queryParams ? `?${queryParams}` : ''}`, null, callback);
  }

  static async addUserList(formdata: any, callback: any) {
    return this.request('POST', '/users', formdata, callback);
  }

  static async getUserByID(id: number, callback: any) {
    return this.request('GET', `/users/${id}`, null, callback);
  }

  static async updateUserByID(payload: any, id: number, callback: any) {
    return this.request('PUT', `/users/${id}/update`, payload, callback);
  }

  static async updateEmployeeByID(payload: any, id: number, callback: any) {
    return this.request('PUT', `/employee/${id}/update`, payload, callback);
  }

  static async updateRoles(id: number, payload: any, callback: any) {
    return this.request('PUT', `access-operation/${id}/role-operation`, payload, callback);
  }

  static async addProject(formdata: any, callback: any) {
    return this.request('POST', '/project', formdata, callback);
  }

  static async getProjectByID(id: number, callback: any) {
    return this.request('GET', `/project/${id}`, null, callback);
  }

  static async getProjectByClientID(id: number, callback: any) {
    return this.request('GET', `/project/client/${id}`, null, callback);
  }

  static async updateProjectByID(formdata: any, id: number, callback: any) {
    return this.request('PUT', `/project/${id}`, formdata, callback);
  }

  static async setProjectPrefix(id: number, prefix: string | null) {
    return this.request('PATCH', `/project/${id}/prefix`, { prefix: prefix || null }, null, 'application/json');
  }

  static async updateTicketbyAdmin(formdata: any, id: number, callback: any) {
    return this.request('PUT', `/tickets/${id}`, formdata, callback, 'multipart/form-data');
  }

  static async updateTicketbyUser(payload: any, id: number, callback: any) {
    return this.request('PATCH', `/tickets/${id}/user`, payload, callback);
  }

  static async addTicket(formdata: any, Userinfo: any, callback: any) {
    return this.request('POST', '/tickets', formdata, callback, 'multipart/form-data');
  }

  static async allUsers(callback: any) {
    return this.request('GET', '/users/all', null, callback);
  }

  static async tickets(filter: any = null, callback: any) {
    const queryParams = new URLSearchParams(filter).toString();
    return this.request('GET', `/tickets${queryParams ? `?${queryParams}` : ''}`, null, callback);
  }

  static async projectList(filter: any, callback: any) {
    const queryParams = new URLSearchParams(filter).toString();
    return this.request('GET', `/project${queryParams ? `?${queryParams}` : ''}`, null, callback);
  }

  static async addJobDetails(id: number, formdata: any, callback: any) {
    return this.request('POST', `/job-details/${id}`, formdata, callback);
  }

  static async getJobDetails(id: string, callback: any) {
    return this.request('GET', `/job-details/get-one/${id}`, null, callback);
  }

  static async getCommAddressDetails(id: string, callback: any) {
    return this.request('GET', `/address/get/${id}`, null, callback);
  }

  static async getProjectUserByDetails(projectName: string, callback: any) {
    return this.request('GET', `/project-details/user/${projectName}`, null, callback);
  }

  // Get projects for dropdown (active projects only)
  static async getProjectsForDropdown(callback: any) {
    return this.request('GET', '/project-details/dropdown/projects-list', null, callback);
  }

  // Get all projects with pagination
  static async getAllProjects(page: number = 1, limit: number = 100, callback: any) {
    return this.request('GET', `/project?offset=${page}&limit=${limit}`, null, callback);
  }

  static async createDefectCommandWithFilesV2(formData: FormData, callback: any) {
    return this.request('POST', '/defect-tracker-commands/with-files', formData, callback);
  }

  // This should already exist - just verify it's there
  static async getProjectDetails(id: string, callback: any) {
    return this.request('GET', `/project-details/${id}`, null, callback);
  }

  static async getEducationalDetails(id: string, callback: any) {
    return this.request('GET', `/educational/${id}`, null, callback);
  }

  static async getTechnicalSkillsDetails(id: string, callback: any) {
    return this.request('GET', `/technical-skills/${id}`, null, callback);
  }

  static async getProfessionalDetails(id: string, callback: any) {
    return this.request('GET', `/professional-details/${id}`, null, callback);
  }

  static async updateJobDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/job-details/${id}`, formdata, callback);
  }

  static async createJobDetailsHistory(id: number, formdata: any, callback: any) {
    return this.request('POST', `/job-details-history/${id}`, formdata, callback);
  }

  static async getJobdetailsHistory(id: string, callback: any) {
    return this.request('GET', `job-details-history/get-one/${id}`, null, callback);
  }

  static async getNameUnderImmediateSupervisor(immediatesupervisor: string, callback: any) {
    return this.request('GET', `job-details/${immediatesupervisor}`, null, callback);
  }

  static async updateCommAddressDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/address/${id}`, formdata, callback);
  }

  static async updateEducationalDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/educational/${id}`, formdata, callback);
  }

  static async updateTechnicalSkillsDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/technical-skills/${id}`, formdata, callback);
  }

  static async updateProfessionalDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/professional-details/${id}`, formdata, callback);
  }

  static async updateProjectDetails(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/project-details/${id}`, formdata, callback);
  }

  static async update(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/project-details/${id}`, formdata, callback);
  }

  static async getJobDesignation(id: string, callback: any) {
    return this.request('GET', `/job-details-history/${id}/designation`, null, callback);
  }

  static async getJobDepartment(id: string, callback: any) {
    return this.request('GET', `/job-details-history/${id}/department`, null, callback);
  }

  static async getJobPeoplemanager(id: string, callback: any) {
    return this.request('GET', `/job-details-history/${id}/peoplemanager`, null, callback);
  }

  static async getJobImmediatesupervisorHistory(id: string, callback: any) {
    return this.request('GET', `/job-details-history/${id}/immediatesupervisor`, null, callback);
  }

  static async getJobImmediatesupervisor(id: string, callback: any) {
    return this.request('GET', `/job-details/${id}/immediatesupervisor`, null, callback);
  }

  static async getHRDetailsData(id: string, callback: any) {
    return this.request('GET', `/HR-details/${id}`, null, callback);
  }

  static async updateHRDetailsData(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/HR-details/${id}/update`, formdata, callback);
  }

  static async updateImmediateSupervisorHistory(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/job-details-history/${id}/immediatesupervisor`, formdata, callback);
  }

  static async updatepeopleManagerHistory(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/job-details-history/${id}/peoplemanager`, formdata, callback);
  }

  static async updatedesignationHistory(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/job-details-history/${id}/designation`, formdata, callback);
  }

  static async updatedepartmentHistory(id: number, formdata: any, callback: any) {
    return this.request('PUT', `/job-details-history/${id}/department`, formdata, callback);
  }

  static async getEmployeeAttendanceData(searchFilters: SearchQueryFilters, callback: any) {
    const filterAttrs = ['limit', 'offset', 'employeeId', 'query', 'employeeName', 'startDate', 'endDate', 'room', 'workType'];
    const params = filterAttrs.reduce(
      (acc, attr) => {
        if (searchFilters[attr] !== undefined && searchFilters[attr] !== null && searchFilters[attr] !== '') {
          acc[attr] = searchFilters[attr];
        }
        return acc;
      },
      {} as Record<string, any>
    );

    const queryString = new URLSearchParams(params).toString();
    return this.request('GET', `bio-metric-connection${queryString ? `?${queryString}` : ''}`, null, callback);
  }

  static async attendenceUpload(formdata: any, callback: any, state: string) {
    return this.request('POST', `/bio-metric-connection/upload/?state=${state}`, formdata, callback, 'multipart/form-data');
  }
  static async connectToBiometricDevice(state: string, selectedIp: string) {
    const apiPath = selectedIp
      ? `/bio-metric-connection/attendances?state=${state}&ip=${selectedIp}`
      : `/bio-metric-connection/attendances?state=${state}`;

    return this.request('GET', apiPath);
  }

  static async biolastentry(formdata: any, callback: any) {
    return this.request('POST', '/biometriclastsyncentry', formdata, callback, 'multipart/form-data');
  }

  static async getTicketFilter(filter: any, callback: any) {
    return this.request('GET', '/tickets/ticket/data', null, callback, 'application/json', filter);
  }

  static async createUploadData(uploadData: any, file: File, currentTab: string, callback: any) {
    // Initialize uploadData if undefined (Issue #15 fix)
    if (!uploadData) {
      uploadData = {};
    }
    uploadData.uploadFolder = currentTab;

    currentTab = currentTab.substring(0, currentTab.length - 1);
    const path = `/asset`;

    try {
      const result = await this.request('POST', path, uploadData);
      if (!result.data?.signedUrl || !result.data?.assetId) {
        throw new Error('Failed to get signed URL');
      }

      await Axios.put(result.data.signedUrl, file, {
        headers: {
          'Content-Type': file.type
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        }
      });

      const uploadStatus = await this.request(
        'GET',
        `/asset/${result.data.assetId}/get-signed-url`,
        null,
        null,
        'application/json',
        null,
        true
      );
      callback(uploadStatus);
    } catch (error) {
      console.error('Error in createUploadData:', error);
      callback(error);
    }
  }

  static async getAllAssets(callback: any) {
    try {
      const result = await this.request('GET', '/asset');
      callback(result);
    } catch (error) {
      console.error('Error fetching assets:', error);
      callback(error);
    }
  }
  static async getAllAssetsDataOnly(callback: any) {
    try {
      const result = await this.request('GET', '/asset/data-only');
      callback(result);
    } catch (error) {
      console.error('Error fetching assets:', error);
      callback(error);
    }
  }

  // static async createUploadDataBase64(uploadData: any, file: File | string, currentTab: string, callback: any) {
  //   uploadData.uploadFolder = currentTab;
  //   currentTab = currentTab.substring(0, currentTab.length - 1);
  //   const path = `/asset`;

  //   try {

  //     if (typeof file === 'string' && file.startsWith('data:')) {
  //       // Base64 upload
  //       uploadData.base64Data = file;
  //       uploadData.contentType = file.split(';')[0].split(':')[1];
  //       uploadData.title = `base64_upload_${Date.now()}`;
  //     } else if (file instanceof File) {
  //       // Existing file upload logic
  //       uploadData.title = file.name;
  //       uploadData.contentType = file.type;
  //     } else {
  //       throw new Error("Invalid file type");
  //     }
  //     const result = await this.request("POST", path, uploadData);

  //     if (!result.data?.signedUrl || !result.data?.assetId) {
  //       throw new Error("Failed to get signed URL");
  //     }

  //     if (file instanceof File && result.data?.signedUrl) {
  //       await Axios.put(result.data.signedUrl, file, {
  //         headers: {
  //           'Content-Type': file.type,
  //         },
  //         onUploadProgress: (progressEvent) => {
  //           const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
  //         },
  //       });
  //     }
  //     const uploadStatus = await this.request("GET", `/asset/${result.data.assetId}/get-signed-url`, null, null, "application/json", null, false);
  //     callback(uploadStatus);
  //   } catch (error) {
  //       console.error("Error in createUploadData:", error);
  //       callback(error);
  //   }
  // }

  static async createLogoUploadData(uploadData: any, file: File, currentTab: string, callback: any) {
    // Initialize uploadData if undefined (Issue #15 fix)
    if (!uploadData) {
      uploadData = {};
    }
    uploadData.uploadFolder = currentTab;
    currentTab = currentTab.substring(0, currentTab.length - 1);
    const path = `/asset`;

    try {
      const result = await this.request('POST', path, uploadData);
      if (!result.data?.signedUrl || !result.data?.assetId) {
        throw new Error('Failed to get signed URL');
      }

      await Axios.put(result.data.signedUrl, file, {
        headers: {
          'Content-Type': file.type
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        }
      });

      const uploadStatus = await this.request(
        'GET',
        `/asset/${result.data.assetId}/get-signed-url`,
        null,
        null,
        'application/json',
        null,
        false
      );
      callback(uploadStatus);
    } catch (error) {
      console.error('Error in createLogoUploadData:', error);
      callback(error);
    }
  }

  static async createStatusUpdate(data: any): Promise<any> {
    return this.request('POST', '/status-updates', data);
  }

  static async updateStatusUpdate(id: string, data: any, callback?: any) {
    return this.request('PUT', `/status-updates/${id}`, data, callback);
  }
  static async getAssetSignedUrl(assetId: string): Promise<IAsset | null> {
    const result = await this.request('GET', `/asset/${assetId}/get-signed-url`, null, null, 'application/json', null, true);
    return result.data?.asset || null;
  }

  static async getAssetSignedUrlWithCallBack(assetId: string, callback: any): Promise<IAsset | null> {
    const result = await this.request('GET', `/asset/${assetId}/get-signed-url`, null, callback, 'application/json', null, true);
    return result.data?.asset || null;
  }

  static async getPartyData(callback: any) {
    return this.request('GET', '/party', null, callback);
  }

  static async getAllAssetDetails(callback: any) {
    return this.request('GET', '/asset-details', null, callback);
  }

  static async getAssetDetailsPaginated(params: {
    page?: number;
    limit?: number;
    search?: string;
    assetType?: string;
    assetStatus?: string;
  }) {
    const query = new URLSearchParams();
    query.set('page', String(params.page ?? 1));
    query.set('limit', String(params.limit ?? 20));
    if (params.search) query.set('search', params.search);
    if (params.assetType) query.set('assetType', params.assetType);
    if (params.assetStatus) query.set('assetStatus', params.assetStatus);
    return this.request('GET', `/asset-details?${query.toString()}`);
  }

  static async getAssetDetailsById(id: any) {
    const response = await this.request('GET', `/asset-details/${id}`);
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    }
    return false;
  }

  static async createAssetDetails(formdata: any) {
    const response = await this.request('POST', '/asset-details', formdata);
    if (response && 'data' in response) {
      return response.data;
    }
    return null;
  }

  static async updateAssetDetails(id: any, formdata: any) {
    return this.request('PUT', `/asset-details/${id}`, formdata);
  }

  static async deleteAssetDetails(id: any) {
    return this.request('DELETE', `/asset-details/${id}`);
  }

  static async getAssetProfile(assetId: string) {
    return this.request('GET', `/asset-details/profile/${assetId}`);
  }

  // Asset Linking
  static async getLinkedAssets(assetId: number) {
    return this.request('GET', `/asset-details/${assetId}/links`);
  }

  static async linkAsset(parentAssetId: number, payload: { childAssetId: number; notes?: string }) {
    return this.request('POST', `/asset-details/${parentAssetId}/links`, payload);
  }

  static async unlinkAsset(assetId: number, linkId: number) {
    return this.request('DELETE', `/asset-details/${assetId}/links/${linkId}`);
  }

  static async updateAssetCategory(assetId: number, payload: { category: string }) {
    return this.request('PATCH', `/asset-details/${assetId}/category`, payload);
  }

  static async searchAssets(query: string) {
    return this.request('GET', `/asset-details/search?q=${encodeURIComponent(query)}`);
  }

  static async createAssetUtilize(formdata: any) {
    return this.request('POST', '/asset-utilize', formdata);
  }

  static async getAllAssetUtilize(callback: any) {
    return this.request('GET', '/asset-utilize', null, callback);
  }

  static async getAssetUtilizeById(id: any) {
    const response = await this.request('GET', `/asset-utilize/${id}`);
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    }
    return false;
  }

  static async deleteAssetUtilize(id: any) {
    return this.request('DELETE', `/asset-utilize/${id}`);
  }

  static async updateAssetUtilize(id: any, formdata: any) {
    const response = await this.request('PUT', `/asset-utilize/${id}`, formdata, undefined, 'application/json');
    return response;
  }

  static async createAssetService(formdata: any) {
    return this.request('POST', '/asset-service', formdata, undefined, 'application/json');
  }

  static async getAllAssetService(callback: any) {
    return this.request('GET', '/asset-service', null, callback);
  }

  static async getAssetServiceById(id: any) {
    const response = await this.request('GET', `/asset-service/${id}`);
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    }
    return false;
  }

  static async deleteAssetService(id: any) {
    return this.request('DELETE', `/asset-service/${id}`);
  }

  static async updateAssetService(id: any, formdata: any) {
    return this.request('PUT', `/asset-service/edit/${id}`, formdata, undefined, 'application/json');
  }

  static async createAssetExitForm(formdata: any) {
    return this.request('POST', '/asset-exit-form', formdata, undefined, 'application/json');
  }

  static async updateAssetExitForm(id: any, formdata: any) {
    return this.request('PUT', `/asset-exit-form/edit/${id}`, formdata, undefined, 'application/json');
  }

  static async getAllAssetExit(callback: any) {
    return this.request('GET', '/asset-exit-form', null, callback);
  }

  static async getAssetExitDetailsById(id: any) {
    const response = await this.request('GET', `/asset-exit-form/${id}`);
    if (response?.status === 200 || response?.status === 201) {
      return response.data;
    }
    return false;
  }

  static async deleteAssetExit(id: any) {
    return this.request('DELETE', `/asset-exit-form/${id}`);
  }

  static async getLeaveLedgerInfo() {
    const response = await this.request('GET', '/leave_ledger');
    return response?.data;
  }
  static async getMonthlyAttendanceReport(employeeId: any, month: any) {
    return this.request('GET', `/bio-metric-connection/monthly-attendance?month=${encodeURIComponent(month)}&employeeId=${employeeId}`);
  }
  static async getDailyAttendanceBiometricData() {
    return this.request('GET', '/bio-metric-connection/daily-attendance');
  }

  static async getDepartmentLeaves(departmentId: string, month: number, year: number) {
    const params = new URLSearchParams({
      departmentId,
      month: month.toString(),
      year: year.toString()
    }).toString();

    return this.request('GET', `/leave-balance/department-leaves?${params}`);
  }

  static async getCalenderLeaves() {
    return this.request('GET', `leave_transaction`);
  }

  // Holiday Policy
  static async createPolicy(formData: any, callback: any) {
    return this.request('POST', '/policy', formData, callback);
  }
  static async updatePolicyById(id: number, formData, callback: any) {
    return this.request('PUT', `/policy/${id}`, formData, callback);
  }
  static async getAllPolicy(callback: any) {
    return this.request('GET', '/policy', null, callback);
  }
  static async getPolicyById(id: number) {
    return this.request('GET', `/policy/${id}`);
  }

  static async deletePolicy(id: any) {
    return this.request('DELETE', `/policy/delete/${id}`);
  }

  // Holiday calender

  static async getAllHolidays(callback: any) {
    return this.request('GET', '/holiday-calender', null, callback);
  }

  static async createHolidays(payload: any, callback: any) {
    return this.request('POST', '/holiday-calender', payload, callback);
  }

  static async getHolidayCalenderById(id: number) {
    return this.request('GET', `/holiday-calender/${id}`);
  }

  static async updateHolidayById(id: number, payload: any) {
    return this.request('PUT', `/holiday-calender/${id}`, payload);
  }

  static async deleteHolidayCalenderById(id: string, callback: any) {
    return this.request('DELETE', `/holiday-calender/${id}`, null, callback);
  }

  static async getHolidaysByLocation(locationId: number, year: number, callback: any) {
    return this.request('GET', `/holiday-calender/by-location/${locationId}/year/${year}`, null, callback);
  }
  // Chat Bot
  static async chatBotFaq(message: string) {
    return this.request('GET', `/chat-bot/faq?q=${message}`);
  }

  // Shift Plan
  static async createShiftPlan(payload: any, callback: any) {
    return this.request('POST', '/shifts', payload, callback);
  }
  static async getAllShiftPlan(callback: any, params?: { startDate?: string; endDate?: string }) {
    return this.request('GET', '/shifts', null, callback, null, params);
  }
  static async getShiftPlanById(id: string) {
    return this.request('GET', `/shifts/${id}`, null);
  }

  static async getShiftPlanByEmployeeId(employeeId: string) {
    return this.request('GET', `/shifts/employee/${employeeId}`, null);
  }

  static async getShiftPlanByToday(employeeId: string, callback: any) {
    return this.request('GET', `/shifts/today/${employeeId}`, null, callback);
  }

  static async updateShiftPlan(id: number, payload: any) {
    return this.request('PUT', `/shifts/${id}`, payload);
  }

  // shift Timing

  static async getShiftTiming(callback: any) {
    return this.request('GET', '/shift-times', null, callback);
  }

  static async getShiftTimingById(id, callback: any) {
    return this.request('GET', `/shift-times/${id}`, null, callback);
  }

  static async getShiftTimingWithFilter(filters: any, callback: any) {
    return this.request('POST', '/shift-times/filter', filters, callback);
  }

  static async createShiftTiming(payload: any, callback: any) {
    return this.request('POST', '/shift-times', payload, callback);
  }

  static async updateShiftTiming(id, payload: any, callback: any) {
    return this.request('PUT', `/shift-times/${id}`, payload, callback);
  }

  static async deleteShiftTimingById(id, callback: any) {
    return this.request('DELETE', `/shift-times/${id}`, null, callback);
  }

  // Swap Shift Plan
  static async createSwapShift(payload: any, callback: any) {
    return this.request('POST', '/swap-shifts', payload, callback);
  }

  static async updateSwapShift(id: any, payload: any, callback: any) {
    return this.request('PUT', `/swap-shifts/${id}`, payload, callback);
  }

  static async getAllSwapShifts(callback: any, params?: { startDate?: string; endDate?: string }) {
    return this.request('GET', '/swap-shifts', null, callback, null, params);
  }

  static async getSwapShiftById(id: any, callback: any) {
    return this.request('GET', `/swap-shifts/${id}`, null, callback);
  }

  static async getSwapShiftByEmployeeId(employeeId: number, callback: any) {
    return this.request('GET', `/swap-shifts/employee/${employeeId}`, null, callback);
  }

  // Shift Coverage

  static async createShiftCoverage(payload: any, callback: any) {
    return this.request('POST', '/shift-coverage', payload, callback);
  }

  static async createShiftExtendHour(payload: any, callback: any) {
    return this.request('POST', '/shift-coverage/extend-hour', payload, callback);
  }

  static async getAllShiftCoverage(callback: any, params?: { startDate?: string; endDate?: string }) {
    return this.request('GET', '/shift-coverage', null, callback, null, params);
  }

  static async getShiftCoverageByUser(userId: any, callback: any) {
    return this.request('GET', `/shift-coverage/user/${userId}`, null, callback);
  }

  static async getAllShiftCoverageWithExtendHours(callback: any, params?: { startDate?: string; endDate?: string }) {
    return this.request('GET', '/shift-coverage/extend-hour', null, callback, null, params);
  }

  static async getShiftCoverageWithExtendHourByUser(userId: any, callback: any) {
    return this.request('GET', `/shift-coverage/extend-hour/user/${userId}`, null, callback);
  }

  static async getShiftCoverageByUserDate(userId: any, date: string, callback: any) {
    const params = {
      date: date
    };
    return this.request('GET', `/shift-coverage/user/${userId}`, null, callback, null, params);
  }

  static async updateShiftCoverage(id: number, payload: any) {
    return this.request('PUT', `/shift-coverage/${id}`, payload);
  }

  static async updateShiftCoverageManualLogouttime(id: number, payload: any) {
    return this.request('PUT', `/shift-coverage/${id}/end-time`, payload);
  }

  static async updateShiftExtendHours(id: number, payload: any) {
    return this.request('PUT', `/shift-coverage/extend-hour/${id}`, payload);
  }

  static async updateShiftExtendHoursManualLogouttime(id: number, payload: any) {
    return this.request('PUT', `/shift-coverage/extend-hour/${id}/end-time`, payload);
  }

  static async searchTasks(filters: any) {
    return this.request('POST', '/tasks/search', filters);
  }

  // shift Time

  static async getShiftTime(callback: any) {
    return this.request('GET', '/shift-times', null, callback);
  }

  // Shift Leave Type

  static async CreateShiftLeaveType(payload: any, callback: any) {
    return this.request('POST', '/shift-leave-type', payload, callback);
  }

  static async getAllShiftLeaveType(callback: any) {
    return this.request('GET', '/shift-leave-type', null, callback);
  }

  static async verifyLocation(payload: { username: string; latitude: number; longitude: number }, callback: any) {
    return this.request('POST', '/auth/verify-location', payload, callback);
  }

  static async updateShiftLeaveType(id: number, payload: any) {
    return this.request('PUT', `/shift-leave-type/${id}`, payload);
  }
  static async deleteShiftLeaveTypeById(id: string, callback: any) {
    return this.request('DELETE', `/shift-leave-type/${id}`, callback);
  }

  // Attendance

  static async fetchAllAttendance(params, callback: any) {
    return this.request('GET', 'shift-coverage/attendance', null, callback, null, params);
  }

  static async fetchAllAttendanceSummary(params, callback: any) {
    return this.request('GET', 'shift-coverage/attendance-summary', null, callback, null, params);
  }

  static async fetchTodayAttendanceSummary(params, callback: any) {
    return this.request('GET', 'shift-coverage/today-attendance-summary', null, callback, null, params);
  }

  static async getEmployeeSalaryDetails(employeeId: number, params: any, callback: any) {
    return this.request('GET', `employee/salary-details/${employeeId}`, null, callback, null, params);
  }
  // Ticket Transition

  static async CreateTicketTransition(payload: any, callback: any) {
    return this.request('POST', '/ticket-transitions', payload, callback);
  }

  static async CreateTicketHandoverMail(payload: any, callback: any) {
    return this.request('POST', '/ticket-transitions/sendHandoverMail', payload, callback);
  }

  static async getAllTicketTransition(callback: any) {
    return this.request('GET', '/ticket-transitions', null, callback);
  }

  static async getTicketTransitionById(id: string) {
    return this.request('GET', `/ticket-transitions/${id}`, null);
  }

  static async updateTicketTransitionById(id: number, payload: any) {
    return this.request('PUT', `/ticket-transitions/${id}`, payload);
  }

  static async getTicketTransitionId(id: string) {
    const response = await this.request('GET', `/ticket-transitions/${id}`);
    if (response?.status === HttpStatusCode.Ok) {
      return response?.data;
    }
    return false;
  }

  // Feature Role Access

  static async createFeatureRoleAccess(payload: any, callback: any) {
    return this.request('POST', '/feature-role-access', payload, callback);
  }
  static async getAllFeatureRoleAccess(callback: any) {
    return this.request('GET', '/feature-role-access', null, callback);
  }

  static async getlFeatureRoleAccessByReferenceName(params: any, callback: any) {
    return this.request('GET', '/feature-role-access/reference/name', null, callback, null, params);
  }

  static async getFeatureRoleAccessById(id: string, callback: any) {
    return this.request('GET', `/feature-role-access/${id}`, null, callback);
  }

  static async updateFeatureRoleAccess(id: string, payload: any, callback: any) {
    return this.request('PUT', `/feature-role-access/${id}`, payload, callback);
  }

  // Permission

  static async createPermissionRequest(payload: any, callback: any) {
    return this.request('POST', '/permissions_data', payload, callback);
  }

  static async getAllPermissions(callback: any) {
    return this.request('GET', '/permissions_data', null, callback);
  }

  static async getPermissioByEmployeeId(partyId: string) {
    return this.request('GET', `/permissions_data/party/${partyId}`, null);
  }
  static async updatePermissioById(id: number, payload: any) {
    return this.request('PUT', `/permissions_data/${id}`, payload);
  }

  static async createTicketClosure(payload: any, callback: any) {
    return this.request('POST', '/ticket-closure', payload, callback);
  }

  static async getAllTicketClosureWithFilters(payload: any, callback: any) {
    return this.request('POST', '/ticket-closure/search', payload, callback);
  }

  static async updateTicketClosure(id: number, payload: any, callback: any) {
    return this.request('PUT', `/ticket-closure/${id}`, payload, callback);
  }

  static async getTicketClosure(page = 1, limit = 10, callback: any) {
    const query = `?page=${page}&limit=${limit}`;
    return this.request('GET', `/ticket-closure${query}`, callback);
  }

  static async ticketClosureBulkUpdate(formdata: any[], callback: any) {
    return this.request('POST', '/ticket-closure/bulkupdate', formdata, callback);
  }

  static async postPermissioReplyById(id: number, payload: any) {
    return this.request('POST', `/permissions_data/${id}/reply`, payload);
  }

  static async getDefectTrackerList(page, limit, callback: any) {
    const query = `?page=${page}&limit=${limit}`;
    return this.request('GET', `/defect-tracker${query}`, null, callback);
  }

  static async createDefect(payload: any, callback: any) {
    return this.request('POST', `/defect-tracker`, payload, callback);
  }

  static async getDefectById(id: string, callback: any) {
    return this.request('GET', `/defect-tracker/${id}`, null, callback);
  }

  // Update defect
  static async updateDefect(id: string, payload: any, callback: any) {
    return this.request('PUT', `/defect-tracker/${id}`, payload, callback);
  }
  static async createDefectCommand(payload: any, callback?: any) {
    return this.request('POST', '/defect-tracker-commands', payload, callback);
  }

  static async createDefectWithFiles(formData: FormData, callback: any) {
    return this.request('POST', `/defect-tracker`, formData, callback);
  }

  // Update defect with files
  static async updateDefectWithFiles(id: number, formData: FormData, callback: any) {
    return this.request('PUT', `/defect-tracker/${id}`, formData, callback);
  }
  static async uploadCommandFiles(formData: FormData, callback: any) {
    return this.request('POST', `/defect-tracker-commands/upload-files`, formData, callback);
  }

  static async createDefectCommandWithFiles(formData: FormData, callback?: any) {
    return this.request('POST', '/defect-tracker-commands/with-files', formData, callback);
  }

  // task remainder

  static async getTasks(callback: any) {
    return this.request('GET', `/tasks`, null, callback);
  }

  static async getTaskById(id: string, callback: any) {
    return this.request('GET', `/tasks/${id}`, null, callback);
  }

  static async createTask(formData: any, callback: any) {
    return this.request('POST', `/tasks`, formData, callback);
  }

  static async createTaskFromTemplate(formData: any, callback: any) {
    return this.request('POST', `/tasks/template`, formData, callback);
  }

  static async updateTask(id: string, formData: any, callback: any) {
    return this.request('PUT', `/tasks/${id}`, formData, callback);
  }

  static async taskActivation(id: string, action: string, callback: any) {
    return this.request('GET', `/tasks/activation/${id}/${action}`, null, callback);
  }

  static async deleteTask(id: string, callback: any) {
    return this.request('DELETE', `/tasks/${id}`, null, callback);
  }

  static async getOverallStatusCount(params, callback: any) {
    return this.request('GET', `/tasks/status/all-count`, null, callback, null, params);
  }

  static async getClientStatusCount(params, callback: any) {
    return this.request('GET', `/tasks/status/client-count`, null, callback, null, params);
  }

  static async getUserStatusCount(params, callback: any) {
    return this.request('GET', `/tasks/status/user-count`, null, callback, null, params);
  }

  // task templates
  static async getTaskTemplate(callback: any) {
    return this.request('GET', `/task-templates`, null, callback);
  }

  static async getTaskTemplateById(id: string, callback: any) {
    return this.request('GET', `/task-templates/${id}`, null, callback);
  }

  static async getCheckListTask(callback: any) {
    return this.request('GET', `/task-templates/type-checkList`, null, callback);
  }

  static async createTaskTemplate(formData: any, callback: any) {
    return this.request('POST', `/task-templates`, formData, callback);
  }

  static async getTaskTemplateWithFilters(formData: any, callback: any) {
    return this.request('POST', `/task-templates/search`, formData, callback);
  }

  static async searchTaskTemplates(formData: any, callback: any) {
    return this.request('POST', `/task-templates/search`, formData, callback);
  }
  static async updateTaskTemplate(id: string, formData: any, callback: any) {
    return this.request('PUT', `/task-templates/${id}`, formData, callback);
  }

  static async deleteTaskTemplate(id: string, callback: any) {
    return this.request('DELETE', `/task-templates/${id}`, null, callback);
  }

  // Loan Request
  static async createLoanRequest(formData: any, callback: any) {
    return this.request('POST', `/loan-request`, formData, callback);
  }

  static async getLoanRequests(
    params: {
      employeeId?: number;
      statusId?: number;
      page?: number;
      limit?: number;
      withoutPagination?: boolean;
    },
    callback?: any
  ) {
    const query = new URLSearchParams();

    if (params.employeeId) query.append('employeeId', params.employeeId.toString());
    if (params.statusId) query.append('statusId', params.statusId.toString());
    if (params.page) query.append('page', params.page.toString());
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.withoutPagination !== undefined) query.append('withoutPagination', params.withoutPagination ? 'true' : 'false');

    return this.request('GET', `/loan-request?${query.toString()}`, null, callback);
  }

  static async getLoanRequestById(id: any, callback: any) {
    return this.request('GET', `/loan-request/${id}`, null, callback);
  }

  static async updateLoanRequest(id: any, formData: any, callback: any) {
    return this.request('PATCH', `/loan-request/${id}`, formData, callback);
  }

  // Salary Advance

  static async createSalaryAdvance(formData: any, callback: any) {
    return this.request('POST', `/salary-advance`, formData, callback);
  }

  static async getSalaryAdvances(
    params: {
      employeeId?: number;
      statusId?: number;
      page?: number;
      limit?: number;
      withoutPagination?: boolean;
    },
    callback?: any
  ) {
    const query = new URLSearchParams();

    if (params.employeeId) query.append('employeeId', params.employeeId.toString());
    if (params.statusId) query.append('statusId', params.statusId.toString());
    if (params.page) query.append('page', params.page.toString());
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.withoutPagination !== undefined) query.append('withoutPagination', params.withoutPagination ? 'true' : 'false');

    return this.request('GET', `/salary-advance?${query.toString()}`, null, callback);
  }

  static async getSalaryAdvanceById(id: any, callback: any) {
    return this.request('GET', `/salary-advance/${id}`, null, callback);
  }

  static async updateSalaryAdvance(id: any, formData: any, callback: any) {
    return this.request('PATCH', `/salary-advance/${id}`, formData, callback);
  }

  // leave Balance History
  static async createLeaveBalanceHistory(data: any, callback?: any) {
    return this.request('POST', '/leave-balance-history', data, callback);
  }

  static async getLeaveBalanceHistory(
    params: {
      offset?: number;
      limit?: number;
      [key: string]: any;
    },
    callback?: any
  ) {
    const queryParams = new URLSearchParams(params).toString();

    return this.request('GET', `/leave-balance-history`, null, callback, null, params);
  }

  static async getAllLeaveBalanceHistory(callback?: any) {
    return this.request('GET', '/leave-balance-history/all', null, callback);
  }

  static async getLeaveBalanceHistoryByUser(userId: any, leaveTypeId?: number, callback?: any) {
    const params = leaveTypeId ? { leaveTypeId } : null;

    return this.request('GET', `/leave-balance-history/user/${userId}`, null, callback, null, params);
  }

  static async getLeaveBalanceHistoryById(id: number, callback?: any) {
    return this.request('GET', `/leave-balance-history/${id}`, null, callback);
  }

  static async updateLeaveBalanceHistory(id: number, data: any, callback?: any) {
    return this.request('PATCH', `/leave-balance-history/${id}`, data, callback);
  }
}
